import { Injectable } from '@angular/core';
import { HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { BaseService } from '../../helpers/base.service';

export interface ControlledSubstanceBottle {
  id: string;
  lineItemName: string;
  code: string;
  dateModified: Date;
  amountRemaining: number;
  locationId: string;
  location: string;
  medicationSupplyId: string;
  isFinished: boolean;
  bottleId?: string;
  originalBalance?: number;
}

export interface BottlesPagedResponse {
  bottles: ControlledSubstanceBottle[];
  totalCount: number;
  pageNumber: number;
  pageSize: number;
  totalPages: number;
}

export interface BottlesFilterRequest {
  searchTerm?: string;
  locationId?: string;
  includeInactive?: boolean;
  pageNumber?: number;
  pageSize?: number;
  priorityLocationId?: string;
  bottleIdSearch?: string;
  medicationSupplyId?: string;
  branchId?: string;
  isFinishedFilter?: 'all' | 'finished' | 'open';
}

@Injectable({
  providedIn: 'root'
})
export class BottlesService extends BaseService {

  public getBottles(filter: BottlesFilterRequest = {}): Observable<BottlesPagedResponse> {
    const route = `${this.apiUrl}/ControlledSubstanceBottle`;
    let params = new HttpParams();
    
    if (filter.searchTerm) {
      params = params.set('searchTerm', filter.searchTerm);
    }
    
    if (filter.bottleIdSearch) {
      params = params.set('bottleIdSearch', filter.bottleIdSearch);
    }
    
    if (filter.locationId) {
      params = params.set('locationId', filter.locationId);
    }
    
    if (filter.pageNumber !== undefined) {
      params = params.set('pageNumber', filter.pageNumber.toString());
    }
    
    if (filter.pageSize !== undefined) {
      params = params.set('pageSize', filter.pageSize.toString());
    }

    if (filter.priorityLocationId) {
      params = params.set('priorityLocationId', filter.priorityLocationId);
    }

    if (filter.medicationSupplyId) {
      params = params.set('medicationSupplyId', filter.medicationSupplyId);
    }

    if (filter.includeInactive !== undefined) {
      params = params.set('includeInactive', filter.includeInactive.toString());
    }

    if (filter.branchId) {
      params = params.set('branchId', filter.branchId);
    }

    if (filter.isFinishedFilter) {
      params = params.set('isFinishedFilter', filter.isFinishedFilter);
    }

    return this.http.get<BottlesPagedResponse>(route, {
      ...this.headers(),
      params
    }).pipe(map(response => {
      return response;
    }));
  }

  public updateBottle(bottle: ControlledSubstanceBottle): Observable<ControlledSubstanceBottle> {
    const route = `${this.apiUrl}/ControlledSubstanceBottle`;
    const body = JSON.stringify(bottle);

    return this.http.put<ControlledSubstanceBottle>(route, body, this.headers()).pipe(map(response => {
      return response;
    }));
  }

  public getBottleById(id: string): Observable<ControlledSubstanceBottle> {
    const route = `${this.apiUrl}/ControlledSubstanceBottle/${id}`;

    return this.http.get<ControlledSubstanceBottle>(route, this.headers()).pipe(map(response => {
      return response;
    }));
  }

  public createBottle(bottle: Partial<ControlledSubstanceBottle>): Observable<ControlledSubstanceBottle> {
    const route = `${this.apiUrl}/ControlledSubstanceBottle`;
    const body = JSON.stringify(bottle);

    return this.http.post<ControlledSubstanceBottle>(route, body, this.headers()).pipe(map(response => {
      return response;
    }));
  }

  public getNextBottleId(): Observable<string> {
    const route = `${this.apiUrl}/ControlledSubstanceBottle/next-bottle-id`;

    return this.http.get<{ bottleId: string }>(route, this.headers()).pipe(
      map(response => response.bottleId)
    );
  }
}